import React, { useEffect, useRef, useState } from "react";
import { Button, notification } from "antd";
import { useOutletContext } from "react-router-dom";

import PdfModal from "./Component/pdfModel";
import axios from "axios";
import RejectionContent from "./Component/RejectionContent";
import OTPInput from "./Component/OtpInput";
import { confirmOrRejectAppointment } from "../../../api/IndentityApi";
import { Loader2 } from "lucide-react";

const CLOUDFRONT_PREFIX = "https://d1k4lem6cjedfg.cloudfront.net";

const normalizePdfUrl = (url) =>
  typeof url === "string" ? url.replace(CLOUDFRONT_PREFIX, "/net") : null;

const extractOfferLetterUrl = (payload) => {
  if (typeof payload === "string") {
    return normalizePdfUrl(payload);
  }

  const candidates = [
    payload?.data?.offer_letter?.file_url_signed,
    payload?.data?.offer_letter?.file_url,
    payload?.data?.appointment_letter?.file_url_signed,
    payload?.data?.appointment_letter?.file_url,
    payload?.offer_letter?.file_url_signed,
    payload?.offer_letter?.file_url,
    payload?.appointment_letter?.file_url_signed,
    payload?.appointment_letter?.file_url,
    payload?.file_url_signed,
    payload?.file_url,
    payload?.document,
    payload?.url,
  ];

  return normalizePdfUrl(
    candidates.find((value) => typeof value === "string" && value.trim()),
  );
};

export default function OfferLetter() {
  const { responseData } = useOutletContext();

  const [api, contextHolder] = notification.useNotification();
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [reason, setReason] = useState("");
  const type = localStorage.getItem("userType");

  const [isRejectTrue, setIsRejectTrue] = useState(false);
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const pdfObjectUrlRef = useRef(null);
  const pdfRequestSeqRef = useRef(0);

  useEffect(() => {
    return () => {
      if (pdfObjectUrlRef.current) {
        URL.revokeObjectURL(pdfObjectUrlRef.current);
        pdfObjectUrlRef.current = null;
      }
    };
  }, []);

  const clearPdfPreview = () => {
    if (pdfObjectUrlRef.current) {
      URL.revokeObjectURL(pdfObjectUrlRef.current);
      pdfObjectUrlRef.current = null;
    }
    setPdfUrl(null);
  };

  const handleClose = () => {
    pdfRequestSeqRef.current += 1;
    setIsOpen(false);
    setIsRejectTrue(false);
    setLoadingPdf(false);
    clearPdfPreview();
  };

  const handleViewOfferLetter = async () => {
    const requestSeq = ++pdfRequestSeqRef.current;
    const existingPdfUrl = extractOfferLetterUrl(responseData);

    if (existingPdfUrl) {
      clearPdfPreview();
      if (requestSeq !== pdfRequestSeqRef.current) {
        return;
      }
      setPdfUrl(existingPdfUrl);
      setIsOpen(true);
      return;
    }

    setIsOpen(true);
    setLoadingPdf(true);
    clearPdfPreview();

    try {
      const response = await axios.get("/api/employees/employee-offer-letter", {
        responseType: "blob",
      });

      if (requestSeq !== pdfRequestSeqRef.current) {
        return;
      }

      const contentType =
        response?.headers?.["content-type"] || response?.data?.type || "";

      if (
        contentType.includes("application/json") ||
        contentType.includes("text/plain") ||
        contentType.includes("text/html")
      ) {
        const responseText = await response.data.text();
        const parsedResponse = JSON.parse(responseText);
        const parsedPdfUrl = extractOfferLetterUrl(parsedResponse);

        if (!parsedPdfUrl) {
          throw new Error("Appointment letter PDF URL was not found in the response.");
        }

        if (requestSeq !== pdfRequestSeqRef.current) {
          return;
        }
        setPdfUrl(parsedPdfUrl);
        return;
      }

      const objectUrl = URL.createObjectURL(response.data);
      if (requestSeq !== pdfRequestSeqRef.current) {
        URL.revokeObjectURL(objectUrl);
        return;
      }
      pdfObjectUrlRef.current = objectUrl;
      setPdfUrl(objectUrl);
    } catch (error) {
      if (requestSeq !== pdfRequestSeqRef.current) {
        return;
      }
      clearPdfPreview();
      setIsOpen(false);
      api.error({
        message:
          error?.response?.data?.message ||
          error?.message ||
          "Unable to load the Appointment letter PDF.",
        placement: "top",
      });
    } finally {
      if (requestSeq !== pdfRequestSeqRef.current) {
        return;
      }
      setLoadingPdf(false);
    }
  };

  const handleConfirm = async () => {
    setIsLoading(true);
    setOtp(["", "", "", "", "", ""]);
    const { data } = await confirmOrRejectAppointment({
      accept: true,
      reject: false,
    });

    setIsLoading(false);

    if (data?.success) {
      setIsOpen(false);
      setIsOtpSent(true);
      return api.success({
        message: data?.message,
        placement: "top",
      });
    } else {
      setIsOtpSent(false);
      return api.error({
        message: data?.message,
        placement: "top",
      });
    }
  };

  const handleReject = async () => {
    setIsRejecting(true);
    const { data } = await confirmOrRejectAppointment({
      accept: false,
      reject: true,
      reason,
    });

    setIsRejecting(false);

    if (data?.success) {
      setIsOpen(false);
      return api.success({
        message: data?.message,
        placement: "top",
      });
    } else {
      setIsOtpSent(false);
      return api.error({
        message: data?.message,
        placement: "top",
      });
    }
  };

  const handleVerifyOTP = async () => {
    const otpString = otp.join("");

    setIsVerifying(true);
    const { data } = await axios.post("/api/employees/verify-offer", {
      otp: otpString,
    });

    setIsVerifying(false);
    if (data?.success) {
      window.location.reload();
      setIsOtpSent(false);

      api.success({
        message: data?.message,
        placement: "top",
      });
    } else {
      // setIsOtpSent(false);
      return api.error({
        message: data?.message,
        placement: "top",
      });
    }
  };

  return (
    <>
      {contextHolder}

      <div className="align-content-start  w-full ">
        <div>
          <h3 className="color-heading mt-4 mb-3">
            {responseData?.offer_letter?.verified_by_hr === true
              ? "Your Joining Forms have been approved by HR."
              : "Your Joining Form is under process"}
          </h3>
          <div>
            <div className="flex">
              <button
                type="button"
                disabled={
                  loadingPdf ||
                  responseData?.offer_letter?.verified_by_hr === false
                }
                onClick={handleViewOfferLetter}
                className="bg-[#00a0e4] self-center mb-5 mt-4 text-white px-3 py-1 rounded flex items-center"
              >
                View Appointment Letter{" "}
                {loadingPdf && (
                  <span className="w-3 ml-2">
                    <Loader2 className="animate-spin" />
                  </span>
                )}
              </button>
            </div>
          </div>
          {isOtpSent && (
            <div className="flex w-full justify-between">
              <div>
                {" "}
                <h3 className="color-heading mt-4 mb-3">
                  Confirm & Sign via OTP
                </h3>
                <p>We’ll send an OTP to your personal email</p>
              </div>
              <div>
                <div>
                  <div className="flex justify-between">
                    {" "}
                    <p className="mb-1 text-xs">Enter OTP</p>
                    <button
                      onClick={handleConfirm}
                      className="mb-1 text-xs text-red-500"
                    >
                      {" "}
                      Resend
                    </button>
                  </div>

                  <OTPInput otp={otp} onChange={setOtp} length={6} />

                  <div className="flex justify-end">
                    <Button
                      type="button"
                      loading={isVerifying}
                      onClick={handleVerifyOTP}
                      className="bg-[#00a0e4] self-center mb-5 mt-4 text-white px-3 py-1 rounded flex items-center"
                    >
                      Confirm & Sign
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}{" "}
        </div>
      </div>

      <PdfModal
        title="Appointment Letter"
        isOpen={isOpen}
        notTimer={true}
        onClose={handleClose}
        pdfUrl={!isRejectTrue ? pdfUrl : null}
        loadingPdf={loadingPdf}
        notice={
          responseData?.offer_status?.offer_verified === true
            ? null
            : "Please review the Appointment letter carefully before accepting or rejecting."
        }
        iframeTitle="Appointment Letter"
      >
        <div className="justify-between pt-3 items-center">
          <div>
            {(isRejectTrue && (
              <RejectionContent
                reason={reason}
                setReason={setReason}
                handleReject={handleReject}
                isLoading={isRejecting}
                responseData={responseData}
              />
            )) || (
                <>
                  {(responseData?.offer_status?.offer_verified !== true && (
                    <>
                      <p className="text-lg font-bold text-left mx-10">
                        By clicking “Proceed”, you confirm that you are
                        electronically accepting this document. An OTP will be
                        sent to your registered email ID for verification.
                      </p>

                      <div>
                        <Button
                          loading={isLoading}
                          onClick={handleConfirm}
                          type="fill"
                          className="bg-green-600 text-white mr-10"
                        >
                          <span className="mr-2">
                            <svg
                              width="18"
                              height="12"
                              viewBox="0 0 18 12"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M1 6L6.33371 11L17 1"
                                stroke="white"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </span>
                          Proceed
                        </Button>

                        <Button
                          type="fill"
                          onClick={() => setIsRejectTrue(true)}
                          className="bg-red-600 text-white"
                        >
                          <span className="mr-2">
                            <svg
                              width="11"
                              height="11"
                              viewBox="0 0 11 11"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M0.210117 9.77539C-0.0700391 10.0556 -0.0700391 10.5097 0.210117 10.7899C0.490274 11.07 0.944508 11.07 1.22466 10.7899L0.210117 9.77539ZM6.00726 6.00726C6.28742 5.72709 6.28742 5.27293 6.00726 4.99277C5.72709 4.7126 5.27293 4.7126 4.99277 4.99277L6.00726 6.00726ZM4.99277 4.99277C4.7126 5.27293 4.7126 5.72709 4.99277 6.00726C5.27293 6.28742 5.72709 6.28742 6.00726 6.00726L4.99277 4.99277ZM10.7899 1.22466C11.07 0.944508 11.07 0.490274 10.7899 0.210117C10.5097 -0.0700391 10.0556 -0.0700391 9.77539 0.210117L10.7899 1.22466ZM6.00726 4.99277C5.72709 4.7126 5.27293 4.7126 4.99277 4.99277C4.7126 5.27293 4.7126 5.72709 4.99277 6.00726L6.00726 4.99277ZM9.77539 10.7899C10.0556 11.07 10.5097 11.07 10.7899 10.7899C11.07 10.5097 11.07 10.0556 10.7899 9.77539L9.77539 10.7899ZM4.99277 6.00726C5.27293 6.28742 5.72709 6.28742 6.00726 6.00726C6.28742 5.72709 6.28742 5.27293 6.00726 4.99277L4.99277 6.00726ZM1.22466 0.210117C0.944508 -0.0700391 0.490274 -0.0700391 0.210117 0.210117C-0.0700391 0.490274 -0.0700391 0.944508 0.210117 1.22466L1.22466 0.210117ZM1.22466 10.7899L6.00726 6.00726L4.99277 4.99277L0.210117 9.77539L1.22466 10.7899ZM6.00726 6.00726L10.7899 1.22466L9.77539 0.210117L4.99277 4.99277L6.00726 6.00726ZM4.99277 6.00726L9.77539 10.7899L10.7899 9.77539L6.00726 4.99277L4.99277 6.00726ZM6.00726 4.99277L1.22466 0.210117L0.210117 1.22466L4.99277 6.00726L6.00726 4.99277Z"
                                fill="white"
                              />
                            </svg>
                          </span>
                          I Do Not Wish to Proceed
                        </Button>
                      </div>
                    </>
                  )) || (
                      <>
                        {type !== "admin" && (
                          <div className="flex justify-end text-green-600 font-bold">
                            Appointment Letter Accepted
                          </div>
                        )}
                      </>
                    )}
                </>
              )}
          </div>
        </div>
      </PdfModal>
    </>
  );
}
